import {
  type ClipboardEvent,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
} from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { notifyPastedFontsUnavailable } from '@/components/app/editor/toasts'
import { applyEditToParagraphs, applyStyledInsertToParagraphs } from '@/lib/shapes/shape-text-edits'
import { paragraphsToPlainText } from '@/lib/shapes/shape-text'
import { runAtCaret } from '../helpers/caret-geometry'
import {
  finalizeStyledPasteRuns,
  sanitizePlainPaste,
  styledRunsFromClipboardHtml,
} from '../helpers/paste-content'
import type { ShapeTextCharRunMap, ShapeTextFonts } from '../types/shape-text-layout-state'
import type { ShapeTextParagraphsChangeOptions } from './use-shape-text-editing'

/** Paste into this frame: the `paste` event path and the context-menu path. */
export function useShapeTextPaste({
  shapeId,
  paragraphs,
  textareaRef,
  charRunMapRef,
  fontsRef,
  pendingRunStyleRef,
  chainSelectionRange,
  onChainSelectionReplace,
  setTextValue,
  emitParagraphs,
  setCaretIndex,
  setSelectionEndIndex,
}: {
  shapeId: string
  paragraphs: Paragraph[]
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>
  charRunMapRef: MutableRefObject<ShapeTextCharRunMap>
  fontsRef: MutableRefObject<ShapeTextFonts>
  pendingRunStyleRef: MutableRefObject<TextRunStyleUpdate | null>
  chainSelectionRange: { start: number; end: number } | null | undefined
  onChainSelectionReplace: ((text: string) => boolean) | undefined
  setTextValue: (value: string) => void
  emitParagraphs: (
    shapeId: string,
    next: Paragraph[],
    options?: ShapeTextParagraphsChangeOptions
  ) => void
  setCaretIndex: Dispatch<SetStateAction<number>>
  setSelectionEndIndex: Dispatch<SetStateAction<number>>
}) {
  /**
   * Shared paste core for both entry points below.
   *
   * Takes the two clipboard flavours already extracted rather than reading the clipboard itself,
   * so it can run synchronously: a `ClipboardEvent`'s data is only readable during dispatch, and
   * awaiting anything first loses it.
   */
  const applyClipboardPayload = useCallback(
    (html: string, plain: string) => {
      const ta = textareaRef.current

      if (!ta) return

      // A cross-frame selection leaves this textarea collapsed at the anchor, so the offsets
      // below would insert without removing anything and the selected span would survive the
      // paste. Clear the whole range first; the caret then sits where the paste belongs.
      // Refused (the range spans a locked chain member — WMPCM-9728): abort the paste rather
      // than inserting on top of a range that is still there, which would duplicate rather than
      // replace. The owner has already told the user why.
      if (chainSelectionRange != null && onChainSelectionReplace) {
        if (!onChainSelectionReplace('')) return
      }

      const deleteStart = ta.selectionStart ?? 0
      const deleteEnd = ta.selectionEnd ?? deleteStart

      // Writes the pasted model and parks the caret just after the inserted text.
      const commitInsert = (nextParagraphs: Paragraph[], insertedLength: number) => {
        const nextText = paragraphsToPlainText(nextParagraphs)

        setTextValue(nextText)
        emitParagraphs(shapeId, nextParagraphs)
        const caretPos = Math.min(deleteStart + insertedLength, nextText.length)

        requestAnimationFrame(() => {
          ta.setSelectionRange(caretPos, caretPos)
          setCaretIndex(caretPos)
          setSelectionEndIndex(caretPos)
        })
      }

      if (html) {
        const { styledRuns, unresolvedFamilies } = styledRunsFromClipboardHtml(html, {
          // An empty frame has no characters to read a style from, only the blank run seeded with
          // the book's default font — fall back to it (as `applyEditToParagraphs` does for plain
          // text) rather than to `extractRunStyle(undefined)`'s hardcoded AWPCHelvetica 14pt.
          caretRun: runAtCaret(charRunMapRef.current, deleteStart, paragraphs),
          pendingStyle: pendingRunStyleRef.current,
          fonts: fontsRef.current,
        })

        if (styledRuns && unresolvedFamilies.size > 0) {
          notifyPastedFontsUnavailable(unresolvedFamilies)
        }

        if (styledRuns) {
          const runs = finalizeStyledPasteRuns(styledRuns, pendingRunStyleRef.current)

          commitInsert(
            applyStyledInsertToParagraphs(paragraphs, deleteStart, deleteEnd, runs),
            runs.reduce((n, r) => n + r.textContent.length, 0)
          )
          return
        }
      }

      if (!plain) return
      const plainText = sanitizePlainPaste(plain)

      // A style chosen at a collapsed caret but not yet typed (Adobe-style pending typing
      // attributes) must be adopted by a paste too, exactly as the next typed character would —
      // typed input passes this same ref (`use-shape-text-editing.ts`). Plain text carries no
      // formatting of its own, so the pending style applies outright. Not cleared afterwards, so
      // that continuing to type after the paste stays in the chosen style, which is how typing
      // already behaves here (the ref is reset on exiting edit).
      commitInsert(
        applyEditToParagraphs(
          paragraphs,
          deleteStart,
          deleteEnd,
          plainText,
          pendingRunStyleRef.current
        ),
        plainText.length
      )
    },
    [
      chainSelectionRange,
      charRunMapRef,
      emitParagraphs,
      fontsRef,
      onChainSelectionReplace,
      paragraphs,
      pendingRunStyleRef,
      setCaretIndex,
      setSelectionEndIndex,
      setTextValue,
      shapeId,
      textareaRef,
    ]
  )

  /**
   * The real paste path (WMPCM-9199).
   *
   * Paste used to be intercepted as a `mod+V` keydown that called `navigator.clipboard.read()`.
   * That missed every paste the browser raises without that exact chord — the macOS Edit▸Paste
   * menu, Services, dictation, dragging text in, `Shift+Insert` — all of which fell through to
   * the textarea's `onChange` as plain text, losing formatting by construction. It also depended
   * on the async Clipboard API: Safari gates `clipboard.read()` behind a permission affordance
   * and a rejection landed in a bare `catch {}`, degrading a styled paste to nothing at all,
   * while Firefox does not expose it to web content at all.
   *
   * A real `paste` handler has none of those problems: `event.clipboardData` is synchronous,
   * needs no permission, and fires for every one of those gestures.
   */
  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const data = event.clipboardData

      if (!data) return

      event.preventDefault()
      applyClipboardPayload(data.getData('text/html'), data.getData('text/plain'))
    },
    [applyClipboardPayload]
  )

  /**
   * Context-menu "Paste" (`ShapeManagerRef.execShapeTextPaste`). No `ClipboardEvent` exists on
   * this path — a menu click is not a paste gesture — so it is the one caller that still has to
   * ask the async Clipboard API, with all the permission caveats above.
   */
  const execPaste = useCallback(async () => {
    if (!textareaRef.current) return

    try {
      let html = ''
      let plainText = ''

      if (typeof navigator.clipboard.read === 'function') {
        const items = await navigator.clipboard.read()

        for (const item of items) {
          if (item.types.includes('text/html') && !html) {
            html = await (await item.getType('text/html')).text()
          }

          if (item.types.includes('text/plain') && !plainText) {
            plainText = await (await item.getType('text/plain')).text()
          }
        }
      } else {
        plainText = await navigator.clipboard.readText()
      }

      applyClipboardPayload(html, plainText)
    } catch {
      // NotAllowedError or clipboard unavailable → no-op
    }
  }, [applyClipboardPayload, textareaRef])

  return { handlePaste, execPaste }
}
